// src/pages/administrasi/Anggaran.jsx
//
// Bendahara's RAB (Rencana Anggaran Biaya) line-item table for an event.
// Unlike the other Administrasi categories, this isn't a template/merge-tag
// document — it's structured, repeating line-item data (see real RAB
// spreadsheets: section -> {kode, uraian, satuan, harga, qty} rows with
// section subtotals and a grand total), which a form-per-submission engine
// doesn't fit well.
import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { API_BASE_URL } from "../../utils/config";
import CustomSelect from "../../components/CustomSelect";
import RupiahInput from "../../components/RupiahInput";
import RAB_REFERENCE, { referenceKodeMap, referenceUraianList } from "./rabReference";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || JSON.stringify(data) || "Request failed");
  }
  return res.status === 204 ? null : res.json();
}

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

export default function Anggaran() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [section, setSection] = useState("");
  const [kode, setKode] = useState("");
  const [uraian, setUraian] = useState("");
  const [satuan, setSatuan] = useState("");
  const [hargaSatuan, setHargaSatuan] = useState("");
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportedUrl, setExportedUrl] = useState("");

  useEffect(() => {
    apiFetch("/api/administrasi/events/?active_only=1")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setEvents(list);
        if (list.length) setEventId(String(list[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadItems = useCallback(async () => {
    if (!eventId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/api/administrasi/rab-items/?event_id=${eventId}`);
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // A link from one event's export doesn't belong under another event.
  useEffect(() => {
    setExportedUrl("");
    setExportError("");
  }, [eventId]);

  const handleExport = async () => {
    setExportError("");
    setExportedUrl("");
    setExporting(true);
    try {
      const result = await apiFetch(`/api/administrasi/rab-items/export-excel/?event_id=${eventId}`, {
        method: "POST",
      });
      setExportedUrl(result.drive_file_url || "");
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setSection("");
    setKode("");
    setUraian("");
    setSatuan("");
    setHargaSatuan("");
    setQty(1);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setSection(it.section || "");
    setKode(it.kode || "");
    setUraian(it.uraian);
    setSatuan(it.satuan || "");
    setHargaSatuan(String(it.harga_satuan));
    setQty(it.qty);
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!uraian.trim()) {
      setFormError("Uraian wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        event: Number(eventId),
        section: section.trim(),
        kode: kode.trim(),
        uraian: uraian.trim(),
        satuan: satuan.trim(),
        harga_satuan: Number(hargaSatuan) || 0,
        qty: Number(qty) || 1,
      };
      if (editingId) {
        await apiFetch(`/api/administrasi/rab-items/${editingId}/`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/api/administrasi/rab-items/", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus item "${it.uraian}"?`)) return;
    try {
      await apiFetch(`/api/administrasi/rab-items/${it.id}/`, { method: "DELETE" });
      loadItems();
    } catch (err) {
      alert(err.message);
    }
  };

  const grouped = items.reduce((acc, it) => {
    const sectionKey = it.section || "Lain-lain";
    const kodeKey = it.kode || "Lain-lain";
    acc[sectionKey] = acc[sectionKey] || {};
    (acc[sectionKey][kodeKey] = acc[sectionKey][kodeKey] || []).push(it);
    return acc;
  }, {});
  const grandTotal = items.reduce((sum, it) => sum + it.total, 0);

  // Datalist suggestions: seeded from rabReference.js, topped up with
  // whatever section/kode/uraian have actually been typed for this event —
  // so a value used once shows up as a suggestion from then on too.
  const sectionOptions = useMemo(() => {
    const used = items.map((it) => it.section).filter(Boolean);
    return Array.from(new Set([...Object.keys(RAB_REFERENCE), ...used]));
  }, [items]);

  const kodeOptions = useMemo(() => {
    const refKodeMap = referenceKodeMap(section) || {};
    const used = items
      .filter((it) => (it.section || "").toLowerCase() === section.trim().toLowerCase())
      .map((it) => it.kode)
      .filter(Boolean);
    return Array.from(new Set([...Object.keys(refKodeMap), ...used]));
  }, [items, section]);

  const uraianOptions = useMemo(() => {
    const refItems = referenceUraianList(section, kode);
    const used = items
      .filter(
        (it) =>
          (it.section || "").toLowerCase() === section.trim().toLowerCase() &&
          (it.kode || "").toLowerCase() === kode.trim().toLowerCase()
      )
      .map((it) => it.uraian)
      .filter(Boolean);
    return Array.from(new Set([...refItems.map((r) => r.uraian), ...used]));
  }, [items, section, kode]);

  const handleUraianChange = (value) => {
    setUraian(value);
    if (satuan.trim()) return;
    const match = referenceUraianList(section, kode).find(
      (r) => r.uraian.toLowerCase() === value.trim().toLowerCase()
    );
    if (match) setSatuan(match.satuan);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <h1 className="text-2xl font-semibold">Anggaran (RAB)</h1>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            onClick={handleExport}
            disabled={!eventId || items.length === 0 || exporting}
            className="shrink-0 px-4 py-2 rounded-lg border border-brand-600 text-brand-600 text-sm font-semibold hover:bg-brand-600/10 disabled:opacity-50"
          >
            {exporting ? "Mengirim ke Drive..." : "Generate Excel & Push ke Drive"}
          </button>
          <button
            onClick={openCreate}
            disabled={!eventId}
            className="shrink-0 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
          >
            + Tambah Item
          </button>
        </div>
      </div>

      {exportedUrl && (
        <div className="glass-card p-3 mb-4 text-sm flex items-center justify-between gap-3">
          <span>Pengajuan Anggaran berhasil dibuat & diupload ke Drive.</span>
          <a
            href={exportedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-brand-600 hover:underline font-medium"
          >
            Buka di Drive
          </a>
        </div>
      )}

      <div className="glass-card p-4 mb-4">
        <label className="block text-xs font-medium mb-1">Event</label>
        <CustomSelect
          value={eventId}
          onChange={setEventId}
          options={events.map((ev) => ({ value: String(ev.id), label: ev.name }))}
          placeholder="-- Pilih event --"
        />
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {exportError && <p className="text-sm text-red-600 mb-3">{exportError}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {Object.entries(grouped).map(([sectionName, kodeGroups]) => {
        const sectionSubtotal = Object.values(kodeGroups)
          .flat()
          .reduce((s, r) => s + r.total, 0);
        return (
          <div key={sectionName} className="glass-card p-4 mb-4">
            <h2 className="text-sm font-semibold mb-2">{sectionName}</h2>
            <div className="glass-table-wrap overflow-x-auto">
              <table className="glass-table text-xs">
                <thead>
                  <tr>
                    <th className="text-left">Uraian</th>
                    <th className="text-right">Qty</th>
                    <th className="text-left">Satuan</th>
                    <th className="text-right">Harga Satuan</th>
                    <th className="text-right">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(kodeGroups).map(([kodeName, rows]) => {
                    const kodeSubtotal = rows.reduce((s, r) => s + r.total, 0);
                    return (
                      <Fragment key={kodeName}>
                        <tr className="bg-black/5 dark:bg-white/5">
                          <td
                            colSpan={6}
                            className="px-2 py-1 font-medium text-[11px] uppercase tracking-wide text-muted"
                          >
                            {kodeName}
                          </td>
                        </tr>
                        {rows.map((it) => (
                          <tr key={it.id} className="border-t">
                            <td className="px-2 py-1.5">{it.uraian}</td>
                            <td className="px-2 py-1.5 text-right">{it.qty}</td>
                            <td className="px-2 py-1.5">{it.satuan}</td>
                            <td className="px-2 py-1.5 text-right">{fmtRp(it.harga_satuan)}</td>
                            <td className="px-2 py-1.5 text-right font-medium">{fmtRp(it.total)}</td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                              <button
                                onClick={() => openEdit(it)}
                                className="text-brand-600 hover:underline mr-2"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(it)}
                                className="text-red-500 hover:underline"
                              >
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t">
                          <td colSpan={3} className="px-2 py-1 text-right text-muted">
                            Subtotal {kodeName}
                          </td>
                          <td className="px-2 py-1 text-right text-muted" colSpan={2}>
                            {fmtRp(kodeSubtotal)}
                          </td>
                          <td></td>
                        </tr>
                      </Fragment>
                    );
                  })}
                  <tr className="border-t font-semibold">
                    <td colSpan={3} className="px-2 py-1.5 text-right">
                      Subtotal {sectionName}
                    </td>
                    <td className="px-2 py-1.5 text-right" colSpan={2}>
                      {fmtRp(sectionSubtotal)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {!loading && items.length === 0 && (
        <p className="text-sm text-muted">Belum ada item RAB untuk event ini.</p>
      )}

      {items.length > 0 && (
        <div className="glass-card p-4 flex items-center justify-between">
          <span className="font-semibold">TOTAL RAB KESELURUHAN</span>
          <span className="font-semibold text-lg">{fmtRp(grandTotal)}</span>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="glass-card w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? "Edit Item RAB" : "Tambah Item RAB"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">
                  Section (mis. Sekben, Listrik, Sound, Logistik)
                </label>
                <input
                  type="text"
                  list="rab-section-options"
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
                <datalist id="rab-section-options">
                  {sectionOptions.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Kode</label>
                  <input
                    type="text"
                    list="rab-kode-options"
                    value={kode}
                    onChange={(e) => setKode(e.target.value)}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                  <datalist id="rab-kode-options">
                    {kodeOptions.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Satuan</label>
                  <input
                    type="text"
                    value={satuan}
                    onChange={(e) => setSatuan(e.target.value)}
                    placeholder="Buah / Roll / Paket"
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Uraian</label>
                <input
                  type="text"
                  list="rab-uraian-options"
                  value={uraian}
                  onChange={(e) => handleUraianChange(e.target.value)}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
                <datalist id="rab-uraian-options">
                  {uraianOptions.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Qty</label>
                  <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Harga Satuan</label>
                  <RupiahInput value={hargaSatuan} onChange={setHargaSatuan} />
                </div>
              </div>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
